import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

/**
 * Proyecto activo, análogo a CountryContext. Solo guarda el id: la lista la
 * resuelve quien la muestre con `useProjectsByOrganization`, que necesita la
 * organización de la URL y ya cachea por ella.
 *
 * La selección se guarda junto a su organización: un proyecto pertenece a una
 * sola, y arrastrar el de la anterior haría que se guardaran productos o
 * categorías en un proyecto ajeno.
 */
interface ProjectContextType {
  projectId: string | null;
  setProjectId: (id: string | null, organizationId: string) => void;
  /** Descarta la selección si venía de otra organización. */
  keepOnlyFor: (organizationId: string) => void;
}

const ProjectContext = createContext<ProjectContextType | undefined>(undefined);

const STORAGE_KEY = 'selectedProject';

interface StoredProject {
  organizationId: string;
  projectId: string;
}

function readStored(): StoredProject | null {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<StoredProject>;
    return parsed.organizationId && parsed.projectId
      ? {organizationId: parsed.organizationId, projectId: parsed.projectId}
      : null;
  } catch {
    return null;
  }
}

interface ProjectProviderProps {
  children: ReactNode;
}

export function ProjectProvider({children}: Readonly<ProjectProviderProps>) {
  const [stored, setStored] = useState<StoredProject | null>(readStored);

  const setProjectId = useCallback(
    (id: string | null, organizationId: string) => {
      const next = id ? {organizationId, projectId: id} : null;
      setStored(next);
      if (next) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }
    },
    [],
  );

  const keepOnlyFor = useCallback((organizationId: string) => {
    setStored(current => {
      if (!current || current.organizationId === organizationId) return current;
      localStorage.removeItem(STORAGE_KEY);
      return null;
    });
  }, []);

  const value = useMemo(
    () => ({projectId: stored?.projectId ?? null, setProjectId, keepOnlyFor}),
    [stored, setProjectId, keepOnlyFor],
  );

  return (
    <ProjectContext.Provider value={value}>{children}</ProjectContext.Provider>
  );
}

export function useSelectedProject() {
  const context = useContext(ProjectContext);
  if (context === undefined) {
    throw new Error(
      'useSelectedProject debe ser utilizado dentro de un ProjectProvider',
    );
  }
  return context;
}
